import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'hr-dashboard.editorRailWidth';

// The floor matches `.editor-rail`'s min-width in input.css — the rail's
// contents (page cards, the scoring inputs) stop being readable below it.
const MIN_WIDTH = 320;
const MAX_WIDTH = 640;
const KEYBOARD_STEP = 16;

function readStoredWidth() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Drag-to-resize for the editor's left rail, in the style of a mail client's
 * or GitHub Desktop's splitter.
 *
 * The width is published as the `--rail-w` custom property on the layout
 * container rather than set on the rail directly, so `.editor-rail` can keep
 * it behind the lg breakpoint: the stacked mobile layout has no handle to drag
 * and must stay full width. A null width means "no custom width yet" and lets
 * the CSS default (20%) stand.
 */
export function useResizableRail() {
  const containerRef = useRef(null);
  const railRef = useRef(null);
  const dragRef = useRef(null);

  const [width, setWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);

  // Mirrors `width` for the handlers, which need the value mid-drag and on
  // pointerup without re-subscribing on every pixel.
  const widthRef = useRef(width);

  // Writes the width straight to the DOM. The editor around this rail is a
  // large tree, so re-rendering it once per pointermove drops the drag well
  // below 60fps; React state is only caught up once the drag ends.
  const writeWidth = useCallback((value) => {
    widthRef.current = value;
    const container = containerRef.current;
    if (!container) return;
    if (value === null) container.style.removeProperty('--rail-w');
    else container.style.setProperty('--rail-w', `${value}px`);
  }, []);

  const commitWidth = useCallback((value) => {
    writeWidth(value);
    setWidth(value);
  }, [writeWidth]);

  const clamp = useCallback((value) => {
    const container = containerRef.current;
    // Never more than half the row: on a narrow laptop the 640px ceiling alone
    // would leave the editor column too cramped to work in.
    const half = container ? Math.round(container.getBoundingClientRect().width / 2) : MAX_WIDTH;
    const ceiling = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, half));
    return Math.round(Math.min(Math.max(value, MIN_WIDTH), ceiling));
  }, []);

  const persist = useCallback((value) => {
    try {
      if (value === null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // Private mode or a full quota: the width simply lives for this session.
    }
  }, []);

  const applyWidth = useCallback((value) => {
    const next = clamp(value);
    commitWidth(next);
    persist(next);
  }, [clamp, commitWidth, persist]);

  const resetWidth = useCallback(() => {
    commitWidth(null);
    persist(null);
  }, [commitWidth, persist]);

  const currentWidth = useCallback(() => (
    widthRef.current ?? railRef.current?.getBoundingClientRect().width ?? MIN_WIDTH
  ), []);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0 || !railRef.current) return;
    // Without this the drag starts a text selection in the editor next to it.
    event.preventDefault();
    dragRef.current = {
      startX: event.clientX,
      startWidth: railRef.current.getBoundingClientRect().width
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsResizing(true);
  }, []);

  const handlePointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag) return;
    writeWidth(clamp(drag.startWidth + (event.clientX - drag.startX)));
  }, [clamp, writeWidth]);

  const endDrag = useCallback((event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsResizing(false);
    setWidth(widthRef.current);
    persist(widthRef.current);
    if (event?.pointerId != null && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, [persist]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      applyWidth(currentWidth() - KEYBOARD_STEP);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      applyWidth(currentWidth() + KEYBOARD_STEP);
    } else if (event.key === 'Home') {
      event.preventDefault();
      resetWidth();
    }
  }, [applyWidth, currentWidth, resetWidth]);

  // While dragging, the col-resize cursor has to survive leaving the handle,
  // and nothing on the page may select.
  useEffect(() => {
    if (!isResizing) return undefined;
    document.body.classList.add('is-rail-resizing');
    return () => document.body.classList.remove('is-rail-resizing');
  }, [isResizing]);

  // A stored width can outgrow its half-the-row ceiling when the window
  // shrinks, so re-clamp it there instead of letting the editor be squeezed.
  const hasCustomWidth = width !== null;
  useEffect(() => {
    if (!hasCustomWidth) return undefined;
    const onResize = () => {
      const next = clamp(currentWidth());
      if (next !== widthRef.current) commitWidth(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [hasCustomWidth, clamp, commitWidth, currentWidth]);

  return {
    containerRef,
    railRef,
    isResizing,
    containerStyle: hasCustomWidth ? { '--rail-w': `${width}px` } : undefined,
    resizerProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onLostPointerCapture: endDrag,
      onDoubleClick: resetWidth,
      onKeyDown: handleKeyDown,
      'aria-valuenow': hasCustomWidth ? width : undefined,
      'aria-valuemin': MIN_WIDTH,
      'aria-valuemax': MAX_WIDTH
    }
  };
}
